<template>
  <div v-if="visible" class="bsw">
    <div class="bsw-head">
      <div class="bsw-title">
        <span class="bsw-icon"><feather-icon icon="TagIcon" size="17" /></span>
        Bird Sales<span v-if="birdTypeLabel"> · {{ birdTypeLabel }}</span>
      </div>
      <b-button variant="primary" size="sm" @click="goListings">
        <feather-icon icon="PlusIcon" size="14" class="mr-25" /> Sell Birds
      </b-button>
    </div>

    <div v-if="loading" class="bsw-loading">
      <b-spinner small variant="primary" />
    </div>

    <template v-else>
      <div class="bsw-grid">
        <div class="bsw-cell">
          <div class="bsw-v text-primary">{{ s.active_listings }}</div>
          <div class="bsw-l">Active listings</div>
        </div>
        <div class="bsw-cell">
          <div class="bsw-v">{{ Number(s.kg_for_sale).toLocaleString('en-IN') }}</div>
          <div class="bsw-l">Kg up for sale</div>
        </div>
        <div class="bsw-cell">
          <div class="bsw-v" :class="s.pending_bids ? 'text-warning' : ''">{{ s.pending_bids }}</div>
          <div class="bsw-l">Pending bids <span v-if="s.pending_bids" class="bsw-attn">review</span></div>
        </div>
        <div class="bsw-cell">
          <div class="bsw-v" :class="s.pending_orders ? 'text-warning' : ''">{{ s.pending_orders }}</div>
          <div class="bsw-l">Orders to approve <span v-if="s.pending_orders" class="bsw-attn">action</span></div>
        </div>
      </div>
      <div class="bsw-foot">
        <span class="text-muted">{{ s.open_for_bids }} open-for-bids · {{ s.fixed_price }} fixed price</span>
        <a href="#" @click.prevent="goListings">Go to Bird Sale ›</a>
      </div>
    </template>
  </div>
</template>

<script>
import axios from '@/libs/axios'

export default {
  name: 'BirdSalesWidget',
  props: {
    birdType: { type: String, default: null }, // broiler | layer | breeder
  },
  data() {
    return {
      loading: true,
      // Shown optimistically while the summary loads (and if it loads
      // successfully); hidden if the backend says the marketplace/bookings
      // module isn't enabled for this company. Previously the widget always
      // rendered and just silently sat at zeros on any error — including a
      // 403 for module-not-enabled — which looked like an active "no
      // activity" feature rather than a feature this company doesn't have.
      visible: true,
      s: {
        active_listings: 0, kg_for_sale: 0, open_for_bids: 0,
        fixed_price: 0, pending_bids: 0, pending_orders: 0,
      },
    }
  },
  computed: {
    birdTypeLabel() {
      if (!this.birdType) return ''
      return this.birdType.charAt(0).toUpperCase() + this.birdType.slice(1)
    },
  },
  created() {
    this.fetchSummary()
  },
  methods: {
    async fetchSummary() {
      this.loading = true
      try {
        const params = this.birdType ? { bird_type: this.birdType } : {}
        const { data } = await axios.get('web/marketplace-summary', { params })
        this.s = data?.data ?? this.s
        this.$emit('summary', this.s) // parent can surface bids/orders in "Needs attention"
      } catch (e) {
        // 403 here specifically means the module.enabled:bookings middleware
        // denied it (company doesn't have the marketplace module) — hide the
        // whole widget rather than show a permanently-empty "Bird Sales"
        // card for a feature this company was never given. Any other error
        // (network blip, 500) stays visible at zeros, matching the original
        // "non-critical widget" behavior.
        if (e.response && e.response.status === 403) {
          this.visible = false
        }
      } finally {
        this.loading = false
      }
    },
    goListings() {
      this.$router.push({ name: 'apps-market-bird-sale' }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.bsw { background: var(--white, #fff); border: 1px solid rgba(34, 41, 47, 0.09); border-radius: 0.857rem; box-shadow: 0 3px 14px rgba(34, 41, 47, 0.06); overflow: hidden; margin-bottom: 4rem; }
.bsw-head { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem 0.75rem; }
.bsw-title { display: flex; align-items: center; gap: 0.55rem; font-size: 1.05rem; font-weight: 700; }
.bsw-icon { width: 30px; height: 30px; border-radius: 8px; background: rgba(41, 109, 180, 0.1); display: flex; align-items: center; justify-content: center; color: #296DB4; }
.bsw-loading { padding: 1.5rem; text-align: center; }
.bsw-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(34, 41, 47, 0.09); border-top: 1px solid rgba(34, 41, 47, 0.09); }
.bsw-cell { background: var(--white, #fff); padding: 1rem 1.25rem; }
.bsw-v { font-size: 1.5rem; font-weight: 800; line-height: 1; font-variant-numeric: tabular-nums; }
.bsw-l { font-size: 0.76rem; color: #6e6b7b; margin-top: 5px; display: flex; align-items: center; gap: 5px; }
.bsw-attn { font-size: 0.62rem; font-weight: 800; color: #ff9f43; background: rgba(255, 159, 67, 0.14); padding: 1px 6px; border-radius: 20px; }
.bsw-foot { padding: 0.75rem 1.25rem; border-top: 1px solid rgba(34, 41, 47, 0.09); display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem; }
.bsw-foot a { color: #296DB4; font-weight: 600; }
@media (max-width: 768px) { .bsw-grid { grid-template-columns: 1fr 1fr; } }
</style>
